import {useState, useMemo, SetStateAction} from "react";

export type ModalAnimationState = "unmounted" | "hidden" | "visible";

type ModalAnimationHandlers = {
	setAnimationState: React.Dispatch<SetStateAction<ModalAnimationState>>;
	setIsOpen: (isOpen: boolean) => void;
	openModal: () => void;
	closeModal: () => void;
	toggleModal: () => void;
};

/**
 * Hook for managing animation state and open state of RAC modals per recommendations in
 * RAC documentation: https://react-spectrum.adobe.com/react-aria/styling.html#framer-motion
 * (i.e. don't use AnimatePresence).
 * @returns An array containing the current open state, the current animation state,
 *          and handlers for changing the animation state, changing the open state,
 *          opening, closing, and toggling the modal.
 */
const useModalAnimation: () => [
	boolean,
	ModalAnimationState,
	ModalAnimationHandlers,
] = () => {
	const [animationState, setAnimationState] =
		useState<ModalAnimationState>("unmounted");
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const handlers = useMemo(
		() => ({
			setAnimationState,
			setIsOpen: (isOpenUpdate: boolean) => {
				setIsOpen(() => isOpenUpdate);
				setAnimationState(() => (isOpenUpdate ? "visible" : "hidden"));
			},
			openModal: () => {
				setIsOpen((currentIsOpen) => {
					if (!currentIsOpen) {
						setAnimationState(() => "visible");
						return true;
					}
					return currentIsOpen;
				});
			},
			closeModal: () => {
				setIsOpen((currentIsOpen) => {
					if (currentIsOpen) {
						setAnimationState(() => "hidden");
						return false;
					}
					return currentIsOpen;
				});
			},
			toggleModal: () => {
				setIsOpen((currentOpenState) => !currentOpenState);
				setAnimationState((currentAnimationState) =>
					currentAnimationState === "visible" ? "hidden" : "visible"
				);
			},
		}),
		[]
	);

	return [isOpen, animationState, handlers];
};

export default useModalAnimation;
